import { styled } from '@mui/material';
import { ButtonPrimitive } from '@figma/fpl-components';
import { borderRadius, color as fplColor, spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { color } from '@/colors';
import { Typography, typographyStyles } from '@/UI/AppTypography/AppTypography';
import { I18N_KEYS } from '@/language/keys';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { FlexCenHorVer, FlexCenVer } from '../styles';
import { MinusIcon, ModifierMinusLargeIcon, ModifierPlusLargeIcon, PlusIcon } from '../Icons';

type Size = 'small' | 'medium' | 'large';

const getTypographyVariant = (size: Size) => {
  switch (size) {
    case 'large':
      return 'h2';
    case 'medium':
      return 'body-std-rg';
    case 'small':
    default:
      return 'body-sm-md';
  }
};

const getIconProps = (size: Size, disabled: boolean) => {
  const iconColor = disabled ? fplColor.iconSecondary : fplColor.icon;
  switch (size) {
    case 'large':
      return { style: { width: 24, height: 24 }, strokeWidth: 1.13, color: iconColor };
    case 'medium':
      return { style: { width: 8, height: 8 }, strokeWidth: 3, color: iconColor };
    case 'small':
      return { style: { width: 8, height: 8 }, strokeWidth: 3, color: iconColor };
  }
};

const getContainerStyle = (size: Size) => {
  return {
    border: `1px solid ${fplColor.border}`,
    borderRadius: size === 'medium' ? '8px' : '4px',
    justifyContent: 'space-between',
    backgroundColor: 'transparent',
    height: size === 'large' ? '56px' : size === 'medium' ? '36px' : '24px',
    width: size === 'large' ? '160px' : size === 'medium' ? '72px' : '85px',
  };
};

const StyledButton = styled('button')(({
  disabled,
  size,
  side,
}: {
  disabled: boolean;
  size: Size;
  side: 'left' | 'right';
}) => {
  return {
    height: '100%',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: disabled ? 'transparent' : fplColor.bgSecondary,
      cursor: disabled ? 'inherit' : 'pointer',
    },
    padding: `0 ${size === 'large' ? '16px' : '8px'}`,
    outline: 'none',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...(side === 'left' && {
      borderTopLeftRadius: size === 'medium' ? '8px' : '4px',
      borderBottomLeftRadius: size === 'medium' ? '8px' : '4px',
    }),
    ...(side === 'right' && {
      borderTopRightRadius: size === 'medium' ? '8px' : '4px',
      borderBottomRightRadius: size === 'medium' ? '8px' : '4px',
    }),
  };
});

/**
 * FPL-native styles for the `fpl-migration`-on branch, matching the "Runs counter" design
 * (`4184:65398`). Hand-composed from `ButtonPrimitive` + FPL tokens: FPL ships no stepper, and
 * `IconButton` is `DisallowRestyle` with a full-bleed wash, so it can't draw the design's inset
 * hover chip. Sizes are rem so the shell scales with the rem-based FPL tokens inside it.
 */
const CHIP_INSET = 'calc(2.5rem / 16)';

/**
 * The stepper control: a 24x24 hit area that washes a 19x19 `radius-small` chip on hover/press.
 *
 * Emotion rather than StyleX, because the wash lives on a child box (`::before`) driven by the
 * *button's* state. StyleX silently drops pseudo-class conditions nested inside a pseudo-element —
 * it emits the `default` value and no `:hover::before` rule at all — so the wash never rendered.
 */
const FplStepperButton = styled(ButtonPrimitive)({
  '--fpl-icon-color': fplColor.icon,
  alignItems: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  height: 'calc(24rem / 16)',
  justifyContent: 'center',
  outline: 'none',
  padding: 0,
  position: 'relative',
  width: 'calc(24rem / 16)',
  '&::before': {
    background: 'transparent',
    borderRadius: borderRadius.small,
    content: '""',
    inset: CHIP_INSET,
    position: 'absolute',
  },
  '&:hover::before': { background: fplColor.bgTransparentHover },
  '&:active::before': { background: fplColor.bgTransparentPressed },
  // The ring sits on the chip, inside the hit area, so no ancestor can clip it.
  '&:focus-visible::before': { outline: `1px solid ${fplColor.borderSelected}`, outlineOffset: 1 },
  '&[aria-disabled="true"]': { '--fpl-icon-color': fplColor.iconDisabled, cursor: 'default' },
  '&[aria-disabled="true"]:hover::before, &[aria-disabled="true"]:active::before': {
    background: 'transparent',
  },
});

const fplStyles = stylex.create({
  container: {
    alignItems: 'center',
    backgroundColor: fplColor.bg,
    boxShadow: `inset 0 0 0 1px ${fplColor.border}`,
    boxSizing: 'border-box',
    display: 'flex',
    gap: spacer.spacer1,
  },
  // `large` keeps the radius it had before the "Runs counter" design landed, which only specifies
  // the small size. Only `small` ships today (the run-count picker).
  containerLarge: { borderRadius: borderRadius.small, height: 'calc(56rem / 16)', width: 'calc(160rem / 16)' },
  containerMedium: { borderRadius: borderRadius.medium, height: 'calc(36rem / 16)', width: 'calc(72rem / 16)' },
  containerSmall: { borderRadius: borderRadius.medium, height: 'calc(24rem / 16)', width: 'calc(70rem / 16)' },
  value: {
    alignItems: 'center',
    color: fplColor.text,
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    minWidth: 0,
  },
  valueDisabled: { color: fplColor.textDisabled },
});

const CONTAINER_SIZE_STYLE = {
  large: fplStyles.containerLarge,
  medium: fplStyles.containerMedium,
  small: fplStyles.containerSmall,
} satisfies Record<Size, unknown>;

const FPL_TYPOGRAPHY = {
  large: typographyStyles.textHeadingMedium,
  medium: typographyStyles.textBodyMedium,
  small: typographyStyles.textBodyMedium,
} satisfies Record<Size, unknown>;

interface CounterProps {
  count: number;
  disabled?: boolean;
  max?: number;
  min?: number;
  onChange: (newValue: number) => void;
  step?: number;
  size?: Size;
}

function Counter({ count, disabled = false, onChange, min, max, step = 1, size = 'small' }: CounterProps) {
  const { t } = useTranslation();
  const isFplMigration = useFplMigrationFlag();

  // Both handlers re-check `disabled` themselves. The min/max clamps below don't cover it (a
  // mid-range count passes both), and on the FPL branch the only other guard is `ButtonPrimitive`
  // suppressing clicks on an `aria-disabled` button — so without this, "no run-count change while a
  // run is in flight" would rest entirely on FPL internals.
  const handleIncrement = useCallback(() => {
    if (disabled) {
      return;
    }
    if (max === undefined) {
      onChange(count + step);
      return;
    }
    if (count < max && count + step <= max) {
      onChange(count + step);
    }
  }, [count, disabled, max, onChange, step]);

  const handleDecrement = useCallback(() => {
    if (disabled) {
      return;
    }
    if (min === undefined) {
      onChange(count - step);
      return;
    }
    if (count > min && count - step >= min) {
      onChange(count - step);
    }
  }, [count, disabled, min, onChange, step]);

  const disabledDecrement = disabled || (min !== undefined && count <= min);
  const disabledIncrement = disabled || (max !== undefined && count >= max);

  if (isFplMigration) {
    const decrementLabel = t(I18N_KEYS.COMMON_COMPONENTS.COUNTER.DECREMENT_ARIA);
    const incrementLabel = t(I18N_KEYS.COMMON_COMPONENTS.COUNTER.INCREMENT_ARIA);

    return (
      <div {...stylex.props(fplStyles.container, CONTAINER_SIZE_STYLE[size])}>
        <FplStepperButton
          aria-label={decrementLabel}
          // `IconButton` derives this from aria-label; on a bare primitive we pass it ourselves so
          // the app's FPL tooltip layer still labels these icon-only controls for pointer users.
          data-tooltip={decrementLabel}
          disabled={disabledDecrement}
          onClick={handleDecrement}
        >
          <ModifierMinusLargeIcon />
        </FplStepperButton>
        <span
          {...stylex.props(fplStyles.value, FPL_TYPOGRAPHY[size], disabled && fplStyles.valueDisabled)}
          aria-live="polite"
        >
          {count}
        </span>
        <FplStepperButton
          aria-label={incrementLabel}
          data-tooltip={incrementLabel}
          disabled={disabledIncrement}
          onClick={handleIncrement}
        >
          <ModifierPlusLargeIcon />
        </FplStepperButton>
      </div>
    );
  }

  return (
    <FlexCenVer sx={getContainerStyle(size)}>
      <StyledButton
        onClick={handleDecrement}
        aria-disabled={disabledDecrement}
        aria-label={t(I18N_KEYS.COMMON_COMPONENTS.COUNTER.DECREMENT_ARIA)}
        size={size}
        disabled={disabledDecrement}
        side="left"
      >
        <MinusIcon {...getIconProps(size, disabledDecrement)} />
      </StyledButton>
      <FlexCenHorVer sx={{ height: '100%' }}>
        <Typography variant={getTypographyVariant(size)} color={disabled ? color.White40_T : fplColor.text}>
          {count}
        </Typography>
      </FlexCenHorVer>
      <StyledButton
        onClick={handleIncrement}
        aria-disabled={disabledIncrement}
        aria-label={t(I18N_KEYS.COMMON_COMPONENTS.COUNTER.INCREMENT_ARIA)}
        size={size}
        disabled={disabledIncrement}
        side="right"
      >
        <PlusIcon {...getIconProps(size, disabledIncrement)} />
      </StyledButton>
    </FlexCenVer>
  );
}

export default Counter;
